import {
  type ChangeEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  UploadIcon,
} from "./Icons";
import type {
  PlaybookCreationAnswer,
  PlaybookCreationInput,
  PlaybookMaterial,
} from "./types";
import "./review.css";

const QUESTIONS = [
  {
    text: "Do you have an existing playbook? Upload it and DI will use it as the foundation. Skip if you do not have one.",
    placeholder: "Or describe your current standard…",
    action: "Read your existing playbook",
    materialType: "Existing playbook",
  },
  {
    text: "Do you have a standard form or template you would like this playbook to align with?",
    placeholder: "Or name the form to align with…",
    action: "Align with your standard form",
    materialType: "Standard form",
  },
  {
    text: "Upload executed precedents — five to ten is the sweet spot. DI supplements the playbook with positions you have actually agreed to: the most favourable becomes the standard, the two or three most common alternatives become fallbacks, and significant outliers are excluded.",
    placeholder: "Describe how DI should use these materials…",
    action: "Derive positions from precedents",
    materialType: "Signed precedent",
  },
];

export interface PlaybookCreationFlowProps {
  authorName?: string;
  initialMaterials?: PlaybookMaterial[];
  busy?: boolean;
  onUploadFiles?: (
    questionIndex: number,
    files: File[]
  ) => void | Promise<void>;
  onBuildPlaybook?: (
    input: PlaybookCreationInput
  ) => void | Promise<void>;
  onCancel?: () => void;
}

export function PlaybookCreationFlow({
  authorName = "A. Reyes",
  initialMaterials = [],
  busy = false,
  onUploadFiles,
  onBuildPlaybook,
  onCancel,
}: PlaybookCreationFlowProps) {
  const [step, setStep] = useState(0);
  const [texts, setTexts] = useState(["", "", ""]);
  const [skipped, setSkipped] = useState([false, false, false]);
  const [materials, setMaterials] = useState(initialMaterials);
  const [building, setBuilding] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const question = QUESTIONS[step];
  const materialsForStep = materials.filter((material) => material.questionIndex === step);

  const answers = useMemo<PlaybookCreationAnswer[]>(
    () => QUESTIONS.map((_item, questionIndex) => ({
      questionIndex,
      text: texts[questionIndex],
      skipped: skipped[questionIndex],
      materials: materials.filter((material) => material.questionIndex === questionIndex),
    })),
    [materials, skipped, texts]
  );
  const resolved = (questionIndex: number) => (
    skipped[questionIndex]
    || Boolean(texts[questionIndex].trim())
    || materials.some((material) => material.questionIndex === questionIndex)
  );
  const allQuestionsResolved = QUESTIONS.every((_item, questionIndex) => resolved(questionIndex));

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !onUploadFiles) return;
    setMaterials((current) => [
      ...current,
      ...files.map((file, index) => ({
        id: `${step}-${file.name}-${file.lastModified}-${index}`,
        questionIndex: step,
        name: file.name,
        meta: `${question.materialType} · ${formatBytes(file.size)}`,
        file,
      })),
    ]);
    await onUploadFiles(step, files);
  }

  function advance(skip: boolean) {
    setSkipped((current) => current.map((value, index) =>
      index === step ? skip : value
    ));
    if (step < QUESTIONS.length - 1) setStep((current) => current + 1);
  }

  async function build() {
    if (!onBuildPlaybook || !allQuestionsResolved) return;
    setBuilding(true);
    try {
      await onBuildPlaybook({ answers, materials });
    } finally {
      setBuilding(false);
    }
  }

  return (
    <section className="di-review di-playbook-agent" aria-label="Playbook creation assistance">
      <div className="di-playbook-agent__conversation">
        <div className="di-playbook-agent__scroll">
          <div className="di-playbook-agent__copy">
            <div className="di-author-line">
              <h2>{authorName}</h2>
              <span>15:04</span>
            </div>
            <p className="di-user-prompt">Help me create a playbook.</p>
            <div className="di-dialog-rule" />
            <div className="di-author-line">
              <h2 className="di-kicker">Document Intelligence</h2>
              <span>planned in 6 steps</span>
            </div>
            <p className="di-agent-copy">
              To build your playbook it helps to know what materials you can
              provide. Answer any or all of the questions below — skip whatever
              does not apply.
            </p>
            <p className="di-muted">Waiting for your response…</p>
          </div>
        </div>

        <div className="di-playbook-question-wrap">
          <div className="di-playbook-question">
            <header>
              <p>{question.text}</p>
              <div className="di-question-nav">
                <span>{step + 1} of {QUESTIONS.length}</span>
                <button
                  aria-label="Previous question"
                  className="di-icon-button"
                  disabled={step === 0}
                  onClick={() => setStep((current) => Math.max(current - 1, 0))}
                  type="button"
                >
                  <ChevronIcon direction="left" />
                </button>
                <button
                  aria-label="Next question"
                  className="di-icon-button"
                  disabled={step === QUESTIONS.length - 1}
                  onClick={() => setStep((current) =>
                    Math.min(current + 1, QUESTIONS.length - 1)
                  )}
                  type="button"
                >
                  <ChevronIcon />
                </button>
                <button
                  aria-label="Close playbook creation"
                  className="di-icon-button"
                  disabled={!onCancel}
                  onClick={onCancel}
                  title={!onCancel ? "Close navigation is not connected." : undefined}
                  type="button"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            </header>
            <input
              accept=".doc,.docx,.pdf,.dotx"
              className="di-visually-hidden"
              multiple
              onChange={(event) => void upload(event)}
              ref={fileInput}
              tabIndex={-1}
              type="file"
            />
            <button
              className="di-playbook-upload-row"
              disabled={!onUploadFiles || busy}
              onClick={() => fileInput.current?.click()}
              title={!onUploadFiles ? "Material upload is not connected." : undefined}
              type="button"
            >
              <span className="di-accent-icon"><UploadIcon /></span>
              Upload files
              <span className="di-spacer" />
              <span>
                {materialsForStep.length
                  ? `${materialsForStep.length} added`
                  : "Drag files here or browse"}
              </span>
            </button>
            <div className="di-playbook-question__answer">
              <label>
                <span className="di-visually-hidden">Answer question {step + 1}</span>
                <input
                  className="di-input"
                  onChange={(event) => {
                    const value = event.target.value;
                    setTexts((current) => current.map((text, index) =>
                      index === step ? value : text
                    ));
                    setSkipped((current) => current.map((item, index) =>
                      index === step ? false : item
                    ));
                  }}
                  placeholder={question.placeholder}
                  value={texts[step]}
                />
              </label>
              <button
                className="di-btn di-btn--secondary"
                disabled={busy}
                onClick={() => advance(true)}
                type="button"
              >
                Skip
              </button>
              {step === QUESTIONS.length - 1 ? (
                <button
                  className="di-btn di-btn--primary"
                  disabled={!onBuildPlaybook || busy || building || !allQuestionsResolved}
                  onClick={() => void build()}
                  title={!onBuildPlaybook ? "Playbook drafting is not connected." : undefined}
                  type="button"
                >
                  {building ? "Building…" : "Build playbook"}
                </button>
              ) : (
                <button
                  className="di-btn di-btn--primary"
                  disabled={busy || !resolved(step)}
                  onClick={() => advance(false)}
                  type="button"
                >
                  Next
                </button>
              )}
            </div>
          </div>
          <div className="di-keyboard-hints" aria-hidden="true">
            <span>↑ ↓ Navigate</span>
            <span>Esc Skip</span>
            <span>↵ Choose</span>
          </div>
        </div>
      </div>

      <aside className="di-playbook-agent__aside">
        <header><h2>Progress</h2></header>
        <div className="di-playbook-progress">
          {QUESTIONS.map((item, index) => (
            <div className="di-playbook-progress__step" key={item.action}>
              <span className="di-progress-marker">
                {resolved(index) ? <CheckIcon /> : <span className="di-upcoming-marker" />}
              </span>
              <span>
                {resolved(index)
                  ? (answers[index].skipped ? `Skipped · ${item.action}` : `Ready · ${item.action}`)
                  : `Waiting for answer · ${item.action}`}
              </span>
            </div>
          ))}
        </div>
        {building && (
          <div className="di-playbook-live-status" role="status">
            <span className="di-spinner" />
            <div>
              <strong>Building the playbook draft</strong>
              <span>Organising the answered questions and uploaded materials into reviewable positions.</span>
            </div>
          </div>
        )}
        <div className="di-playbook-context">
          <h2>Context</h2>
          {materials.length === 0 ? (
            <p className="di-muted">
              Materials you upload appear here, with the rules DI derives from each of them.
            </p>
          ) : (
            <div className="di-material-list">
              {materials.map((material) => (
                <div key={material.id}>
                  <strong>{material.name}</strong>
                  <span>{material.meta || QUESTIONS[material.questionIndex]?.materialType}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </section>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
